import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ImpactPreview from '../components/builder/ImpactPreview';

const WebsiteBuilder = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const [showFullPreview, setShowFullPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    // Branding
    logo_url: '',
    primary_color: 'var(--text-primary)',
    secondary_color: '#C4A574',
    // Hero Section
    hero_image_url: '',
    hero_title: 'Making a Difference Together',
    hero_subtitle: 'Join us in creating positive social impact',
    hero_style: 'gradient', // gradient, image, video
    // Company Section
    show_about: true,
    about_text: 'We are committed to making a positive impact in our community and beyond.',
    company_image_url: '',
    mission_statement: '',
    // Hero Text Position (for drag & drop)
    hero_text_position: { x: 50, y: 50 }, // percentage from center
    hero_text_align: 'center', // left, center, right
    // Testimonials
    show_testimonials: false,
    testimonials: [],
    // Employee Leaderboard
    show_leaderboard: true,
    leaderboard_title: 'Our Impact Champions',
    // Hero Images Gallery
    show_hero_gallery: false,
    hero_images: [],
    // Stats Section
    show_stats: true,
    // CTA
    call_to_action_text: 'Join us in making a difference',
    // Footer
    social_links: {},
    footer_text: '',
    // Featured Charities (NEW)
    featured_campaign_ids: [], // array of campaign IDs to feature (top 3)
    show_featured_section: true,
    featured_section_title: 'Featured Causes',
    // Section Order (NEW) - customizable order of page sections
    section_order: ['stats', 'about', 'trends', 'leaderboard', 'testimonials', 'featured', 'campaigns'],
    // Trends & News Section (NEW)
    show_trends: true,
    trends_title: 'Charity Trends & Insights',
    charity_trends: [],
    industry_stats: [],
    news_items: []
  });
  
  // State for drag-and-drop featured charities reordering
  const [draggedCampaign, setDraggedCampaign] = useState(null);
  
  // State for section ordering drag-and-drop
  const [draggedSection, setDraggedSection] = useState(null);
  
  // AI Generation states
  const [generatingAI, setGeneratingAI] = useState(false);
  const [generatingSection, setGeneratingSection] = useState(null);
  const [showAISetup, setShowAISetup] = useState(false);

  // Charity-related stock images for the gallery
  const stockHeroImages = [
    { url: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800', category: 'charity', label: 'Hands Together' },
    { url: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800', category: 'education', label: 'Education' },
    { url: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=800', category: 'children', label: 'Children' },
    { url: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800', category: 'community', label: 'Community' },
    { url: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?w=800', category: 'donation', label: 'Donation Box' },
    { url: 'https://images.unsplash.com/photo-1509099836639-18ba1795216d?w=800', category: 'volunteer', label: 'Volunteers' },
    { url: 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=800', category: 'nature', label: 'Environment' },
    { url: 'https://images.unsplash.com/photo-1560252829-804f1aedf1be?w=800', category: 'health', label: 'Healthcare' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(userRes.data);

      if (userRes.data.business_id) {
        const [businessRes, campaignsRes, donationsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/businesses/${userRes.data.business_id}`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/campaigns`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/donations`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        
        setBusiness(businessRes.data);
        setCampaigns(campaignsRes.data || []);
        setDonations(donationsRes.data || []);
        
        setFormData(prev => ({
          ...prev,
          logo_url: businessRes.data.logo_url || '',
          hero_image_url: businessRes.data.hero_image_url || '',
          hero_title: businessRes.data.hero_title || prev.hero_title,
          hero_subtitle: businessRes.data.hero_subtitle || prev.hero_subtitle,
          hero_style: businessRes.data.hero_style || 'gradient',
          hero_text_position: businessRes.data.hero_text_position || { x: 50, y: 50 },
          hero_text_align: businessRes.data.hero_text_align || 'center',
          primary_color: businessRes.data.primary_color || '#0C4E8C',
          secondary_color: businessRes.data.secondary_color || '#C4A574',
          about_text: businessRes.data.about_text || prev.about_text,
          company_image_url: businessRes.data.company_image_url || '',
          mission_statement: businessRes.data.mission_statement || '',
          show_about: businessRes.data.show_about !== false,
          show_testimonials: businessRes.data.show_testimonials || false,
          testimonials: businessRes.data.testimonials || [],
          show_leaderboard: businessRes.data.show_leaderboard !== false,
          leaderboard_title: businessRes.data.leaderboard_title || 'Our Impact Champions',
          show_hero_gallery: businessRes.data.show_hero_gallery || false,
          hero_images: businessRes.data.hero_images || [],
          show_stats: businessRes.data.show_stats !== false,
          social_links: businessRes.data.social_links || {},
          footer_text: businessRes.data.footer_text || '',
          call_to_action_text: businessRes.data.call_to_action_text || prev.call_to_action_text,
          // Featured Charities
          featured_campaign_ids: businessRes.data.featured_campaign_ids || [],
          show_featured_section: businessRes.data.show_featured_section !== false,
          featured_section_title: businessRes.data.featured_section_title || 'Featured Causes',
          // Trends & News
          show_trends: businessRes.data.show_trends !== false,
          trends_title: businessRes.data.trends_title || 'Charity Trends & Insights',
          charity_trends: businessRes.data.charity_trends || [],
          industry_stats: businessRes.data.industry_stats || [],
          news_items: businessRes.data.news_items || [],
          // Section Order
          section_order: businessRes.data.section_order || ['stats', 'about', 'trends', 'leaderboard', 'testimonials', 'featured', 'campaigns']
        }));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Calculate employee leaderboard from donations
  const employeeLeaderboard = React.useMemo(() => {
    const employeeDonations = donations.filter(d => d.donor_type === 'employee' || d.donor_name);
    const totals = {};
    
    employeeDonations.forEach(d => {
      const name = d.donor_name || 'Anonymous';
      if (!totals[name]) {
        totals[name] = { name, total: 0, count: 0, avatar: d.donor_avatar || null };
      }
      totals[name].total += d.amount || 0;
      totals[name].count += 1;
    });
    
    const sorted = Object.values(totals).sort((a, b) => b.total - a.total).slice(0, 5);
    
    // If no real data, show sample data
    if (sorted.length === 0) {
      return [
        { name: 'Sarah Mitchell', total: 2500, count: 8, avatar: null },
        { name: 'James Chen', total: 1800, count: 6, avatar: null },
        { name: 'Emily Rodriguez', total: 1500, count: 5, avatar: null },
        { name: 'Michael Thompson', total: 1200, count: 4, avatar: null },
        { name: 'Lisa Park', total: 950, count: 3, avatar: null }
      ];
    }
    return sorted;
  }, [donations]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload/logo`,
        formDataObj,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );

      setFormData(prev => ({
        ...prev,
        logo_url: response.data.logo_url
      }));
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload/hero`,
        formDataObj,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );

      setFormData(prev => ({
        ...prev,
        [field]: response.data.hero_image_url
      }));
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.business_id) return;

    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/businesses/${user.business_id}`,
        formData,
        { withCredentials: true }
      );

      alert('Changes saved successfully!');
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    await handleSave();
    const slug = business?.slug || user?.business_id;
    window.open(`/impact/${slug}`, '_blank');
  };

  // AI Generation Functions
  const generateFullWebsite = async () => {
    setGeneratingAI(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/auto-generate-website`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const content = response.data.generated_content;
        const website = response.data.website;
        const colorsExtracted = response.data.colors_extracted_from_logo;
        
        setFormData(prev => ({
          ...prev,
          hero_title: content.hero_title || prev.hero_title,
          hero_subtitle: content.hero_subtitle || prev.hero_subtitle,
          about_text: content.about_text || prev.about_text,
          mission_statement: content.mission_statement || prev.mission_statement,
          call_to_action_text: content.call_to_action_text || prev.call_to_action_text,
          leaderboard_title: content.leaderboard_title || prev.leaderboard_title,
          primary_color: website?.primary_color || prev.primary_color,
          secondary_color: website?.secondary_color || prev.secondary_color,
          show_testimonials: content.testimonials?.length > 0,
          testimonials: content.testimonials || prev.testimonials,
          // Trends & News
          show_trends: (content.charity_trends?.length > 0 || content.industry_stats?.length > 0 || content.news_items?.length > 0),
          charity_trends: content.charity_trends || prev.charity_trends,
          industry_stats: content.industry_stats || prev.industry_stats,
          news_items: content.news_items || prev.news_items,
          show_about: true,
          show_stats: true,
          show_leaderboard: true
        }));
        
        setShowAISetup(false);
        
        if (colorsExtracted) {
          alert('✨ AI-generated Impact Website ready! Colors matched to your logo. Review and customize as needed.');
        } else {
          alert('✨ AI-generated Impact Website ready! Tip: Upload your company logo in the Branding tab to automatically match colors.');
        }
      }
    } catch (error) {
      console.error('AI generation error:', error);
      alert('Error generating content. Please try again.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const generateSection = async (section) => {
    setGeneratingSection(section);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/generate-website-content`,
        { section, regenerate: true },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const content = response.data.content;
        
        if (section === 'hero') {
          setFormData(prev => ({
            ...prev,
            hero_title: content.hero_title || prev.hero_title,
            hero_subtitle: content.hero_subtitle || prev.hero_subtitle
          }));
        } else if (section === 'about') {
          setFormData(prev => ({
            ...prev,
            about_text: content.about_text || prev.about_text,
            mission_statement: content.mission_statement || prev.mission_statement
          }));
        } else if (section === 'testimonials') {
          setFormData(prev => ({
            ...prev,
            testimonials: content.testimonials || prev.testimonials,
            show_testimonials: true
          }));
        } else if (section === 'trends') {
          setFormData(prev => ({
            ...prev,
            charity_trends: content.charity_trends || prev.charity_trends,
            industry_stats: content.industry_stats || prev.industry_stats,
            news_items: content.news_items || prev.news_items,
            show_trends: true
          }));
        }
      }
    } catch (error) {
      console.error('Section generation error:', error);
      alert('Error generating content. Please try again.');
    } finally {
      setGeneratingSection(null);
    }
  };

  const addTestimonial = () => {
    setFormData(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, { name: '', role: '', text: '', avatar: '', rating: 5 }]
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((t, i) => i === index ? { ...t, [field]: value } : t)
    }));
  };

  const removeTestimonial = (index) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
  };

  const selectStockImage = (url) => {
    setFormData(prev => ({ ...prev, hero_image_url: url, hero_style: 'image' }));
  };

  const totalRaised = campaigns.reduce((sum, c) => sum + (c.raised_amount || 0), 0);
  const totalGoal = campaigns.reduce((sum, c) => sum + (c.goal_amount || 0), 0);
  const avgESG = campaigns.length > 0 
    ? Math.round(campaigns.reduce((s, c) => s + (c.esg_score || 75), 0) / campaigns.length)
    : 85;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-canvas)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--brand-primary)' }}></div>
      </div>
    );
  }

  const tabs = [
    { id: 'branding', label: '🎨 Branding', icon: '🎨' },
    { id: 'hero', label: '🖼️ Hero Section', icon: '🖼️' },
    { id: 'layout', label: '📐 Layout', icon: '📐' },
    { id: 'content', label: '📝 Content', icon: '📝' },
    { id: 'trends', label: '📊 Trends & News', icon: '📊' },
    { id: 'featured', label: '⭐ Featured', icon: '⭐' },
    { id: 'testimonials', label: '💬 Testimonials', icon: '💬' },
    { id: 'leaderboard', label: '🏆 Leaderboard', icon: '🏆' },
    { id: 'advanced', label: '⚙️ Advanced', icon: '⚙️' }
  ];

  // Helper functions for Featured Charities drag-and-drop
  const handleDragStart = (campaignId) => {
    setDraggedCampaign(campaignId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (targetCampaignId) => {
    if (!draggedCampaign || draggedCampaign === targetCampaignId) return;
    
    const currentFeatured = [...formData.featured_campaign_ids];
    const draggedIndex = currentFeatured.indexOf(draggedCampaign);
    const targetIndex = currentFeatured.indexOf(targetCampaignId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Reorder
    currentFeatured.splice(draggedIndex, 1);
    currentFeatured.splice(targetIndex, 0, draggedCampaign);
    
    setFormData(prev => ({ ...prev, featured_campaign_ids: currentFeatured }));
    setDraggedCampaign(null);
  };

  const toggleFeaturedCampaign = (campaignId) => {
    setFormData(prev => {
      const current = [...prev.featured_campaign_ids];
      const index = current.indexOf(campaignId);
      
      if (index > -1) {
        // Remove from featured
        current.splice(index, 1);
      } else {
        // Add to featured (max 3)
        if (current.length < 3) {
          current.push(campaignId);
        }
      }
      
      return { ...prev, featured_campaign_ids: current };
    });
  };

  // Get featured campaigns in order
  const featuredCampaigns = formData.featured_campaign_ids
    .map(id => campaigns.find(c => c.campaign_id === id))
    .filter(Boolean);

  // Section ordering functions
  const sectionConfig = [
    { id: 'stats', label: '📊 Stats', icon: '📊', description: 'Impact statistics' },
    { id: 'about', label: '📝 About Us', icon: '📝', description: 'Company information' },
    { id: 'trends', label: '📈 Trends & News', icon: '📈', description: 'Charity trends & insights' },
    { id: 'leaderboard', label: '🏆 Leaderboard', icon: '🏆', description: 'Top contributors' },
    { id: 'testimonials', label: '💬 Testimonials', icon: '💬', description: 'Customer quotes' },
    { id: 'featured', label: '⭐ Featured', icon: '⭐', description: 'Featured campaigns' },
    { id: 'campaigns', label: '🎯 Campaigns', icon: '🎯', description: 'All campaigns' }
  ];

  const handleSectionDragStart = (sectionId) => {
    setDraggedSection(sectionId);
  };

  const handleSectionDragOver = (e) => {
    e.preventDefault();
  };

  const handleSectionDrop = (targetSectionId) => {
    if (!draggedSection || draggedSection === targetSectionId) return;
    
    const currentOrder = [...formData.section_order];
    const draggedIndex = currentOrder.indexOf(draggedSection);
    const targetIndex = currentOrder.indexOf(targetSectionId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Reorder
    currentOrder.splice(draggedIndex, 1);
    currentOrder.splice(targetIndex, 0, draggedSection);
    
    setFormData(prev => ({ ...prev, section_order: currentOrder }));
    setDraggedSection(null);
  };

  const moveSectionUp = (sectionId) => {
    const currentOrder = [...formData.section_order];
    const index = currentOrder.indexOf(sectionId);
    if (index <= 0) return;
    
    [currentOrder[index - 1], currentOrder[index]] = [currentOrder[index], currentOrder[index - 1]];
    setFormData(prev => ({ ...prev, section_order: currentOrder }));
  };

  const moveSectionDown = (sectionId) => {
    const currentOrder = [...formData.section_order];
    const index = currentOrder.indexOf(sectionId);
    if (index === -1 || index >= currentOrder.length - 1) return;
    
    [currentOrder[index], currentOrder[index + 1]] = [currentOrder[index + 1], currentOrder[index]];
    setFormData(prev => ({ ...prev, section_order: currentOrder }));
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-canvas)' }}>
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Impact Website Builder</h1>
                <p className="text-sm text-gray-500">Create a stunning public page for your social impact</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAISetup(true)}
                disabled={generatingAI}
                className="px-4 py-2 rounded-lg font-medium text-white transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' }}
                data-testid="ai-generate-btn"
              >
                {generatingAI ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </span>
                ) : '✨ AI Generate'}
              </button>
              <button
                onClick={() => setShowFullPreview(!showFullPreview)}
                className="px-4 py-2 rounded-lg font-medium border-2 transition-all hover:bg-gray-50"
                style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}
              >
                {showFullPreview ? '← Back to Editor' : '👁️ Full Preview'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg font-medium text-white transition-all disabled:opacity-50"
                style={{ background: 'var(--brand-primary)' }}
              >
                {saving ? 'Saving...' : '💾 Save Changes'}
              </button>
              <button
                onClick={handlePublish}
                className="px-4 py-2 rounded-lg font-bold text-white transition-all"
                style={{ background: 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' }}
              >
                🚀 Publish & Preview
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* AI Generation Setup Modal */}
      {showAISetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAISetup(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 text-center" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' }}>
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-2xl font-bold text-white mb-2">AI Website Generator</h2>
              <p className="text-purple-100">Create a stunning Impact Website in seconds</p>
            </div>
            
            <div className="p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                  <div className="text-2xl">🎯</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">AI-Generated Content</h3>
                    <p className="text-sm text-gray-600">Hero text, about section, mission statement, and sample testimonials tailored to your company profile.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="text-2xl">🎨</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Logo-Matched Colors</h3>
                    <p className="text-sm text-gray-600">Colors automatically extracted from your company logo for brand consistency.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl">✏️</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Fully Customizable</h3>
                    <p className="text-sm text-gray-600">Edit any AI-generated content. Use &ldquo;Generate with AI&rdquo; buttons to regenerate specific sections.</p>
                  </div>
                </div>
              </div>
              
              {!formData.logo_url && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  💡 <strong>Tip:</strong> Upload your logo first to automatically match your brand colors!
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAISetup(false)}
                  className="flex-1 px-4 py-3 rounded-xl font-medium border-2 text-gray-600 hover:bg-gray-50 transition-all"
                  style={{ borderColor: 'var(--border-subtle)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={generateFullWebsite}
                  disabled={generatingAI}
                  className="flex-1 px-4 py-3 rounded-xl font-bold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' }}
                  data-testid="confirm-ai-generate-btn"
                >
                  {generatingAI ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Generating...
                    </span>
                  ) : '✨ Generate Website'}
                </button>
              </div>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                Sample testimonials will be clearly labeled as such
              </p>
            </div>
          </div>
        </div>
      )}

      {showFullPreview ? (
        // Full Preview Mode - alt-no-theme: the customer's own published site,
        // rendered exactly as their visitors will see it, not in our theme
        <div className="bg-white alt-no-theme">
          <ImpactPreview formData={formData} business={business} campaigns={campaigns} leaderboard={employeeLeaderboard} totalRaised={totalRaised} totalGoal={totalGoal} avgESG={avgESG} />
        </div>
      ) : (
        // Editor Mode
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Tabs Bar - Full Width Across Page */}
          <div className="bg-white rounded-xl shadow-sm border p-1.5 mb-6 flex justify-between gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all text-center ${
                  activeTab === tab.id 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* Left Panel - Editor */}
            <div className="col-span-5">

              <div className="bg-white rounded-xl shadow-sm border p-6 max-h-[calc(100vh-220px)] overflow-y-auto">
                {/* BRANDING TAB */}
                {activeTab === 'branding' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-900">Brand Identity</h3>
                    
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Company Logo</label>
                      <div className="border-2 border-dashed rounded-xl p-6 text-center transition-all hover:border-blue-400" style={{ borderColor: 'var(--border-subtle)' }}>
                        {formData.logo_url ? (
                          <div className="space-y-3">
                            <img src={formData.logo_url} alt="Logo" className="h-16 mx-auto object-contain" />
                            <div className="flex gap-2 justify-center flex-wrap">
                              <label className="px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer text-blue-600 hover:bg-blue-50">
                                Change Logo
                                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                              </label>
                              <button 
                                onClick={async () => {
                                  try {
                                    const response = await axios.post(
                                      `${BACKEND_URL}/api/extract-colors`,
                                      { image_url: formData.logo_url },
                                      { withCredentials: true }
                                    );
                                    if (response.data.primary && response.data.secondary) {
                                      setFormData(prev => ({
                                        ...prev,
                                        primary_color: response.data.primary,
                                        secondary_color: response.data.secondary
                                      }));
                                      alert('✨ Colors extracted from logo!');
                                    } else {
                                      alert('Could not extract colors. Try a different logo image.');
                                    }
                                  } catch (error) {
                                    alert('Could not extract colors. Make sure your logo URL is accessible.');
                                  }
                                }}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg text-purple-600 hover:bg-purple-50 flex items-center gap-1"
                                data-testid="extract-colors-btn"
                              >
                                🎨 Extract Colors
                              </button>
                              <button 
                                onClick={() => setFormData(prev => ({ ...prev, logo_url: '' }))}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="cursor-pointer block">
                            <div className="text-4xl mb-2">📤</div>
                            <p className="font-medium text-gray-700">Upload your logo</p>
                            <p className="text-xs text-gray-400 mt-1">PNG, SVG, or JPG (recommended 400x100px)</p>
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">Primary Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.primary_color}
                            onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                            className="w-12 h-12 rounded-lg border-2 cursor-pointer"
                            style={{ borderColor: 'var(--border-subtle)' }}
                          />
                          <input
                            type="text"
                            value={formData.primary_color}
                            onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                            className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                            style={{ borderColor: 'var(--border-subtle)' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">Secondary Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.secondary_color}
                            onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                            className="w-12 h-12 rounded-lg border-2 cursor-pointer"
                            style={{ borderColor: 'var(--border-subtle)' }}
                          />
                          <input
                            type="text"
                            value={formData.secondary_color}
                            onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                            className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                            style={{ borderColor: 'var(--border-subtle)' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Color Presets */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Quick Color Presets</label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { p: '#0C4E8C', s: '#C4A574', label: 'Corporate Blue' },
                          { p: '#10B981', s: '#059669', label: 'Nature Green' },
                          { p: '#8B5CF6', s: '#A855F7', label: 'Royal Purple' },
                          { p: '#F59E0B', s: '#D97706', label: 'Warm Gold' }
                        ].map((preset, i) => (
                          <button
                            key={i}
                            onClick={() => setFormData(prev => ({ ...prev, primary_color: preset.p, secondary_color: preset.s }))}
                            className="p-2 rounded-lg border text-center hover:shadow-md transition-all"
                            style={{ borderColor: 'var(--border-subtle)' }}
                          >
                            <div className="flex gap-1 justify-center mb-1">
                              <div className="w-4 h-4 rounded" style={{ background: preset.p }} />
                              <div className="w-4 h-4 rounded" style={{ background: preset.s }} />
                            </div>
                            <p className="text-[10px] text-gray-500">{preset.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* HERO SECTION TAB */}
                {activeTab === 'hero' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Hero Section</h3>
                      <button
                        onClick={() => generateSection('hero')}
                        disabled={generatingSection === 'hero'}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 transition-all disabled:opacity-50 flex items-center gap-1"
                        data-testid="generate-hero-btn"
                      >
                        {generatingSection === 'hero' ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Generating...
                          </>
                        ) : (
                          <>✨ Generate with AI</>
                        )}
                      </button>
                    </div>
                    
                    {/* Hero Style */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Hero Style</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'gradient', label: '🎨 Gradient', desc: 'Uses your brand colors' },
                          { id: 'image', label: '🖼️ Image', desc: 'Custom background' },
                          { id: 'minimal', label: '🎯 Minimal', desc: 'Clean & simple' }
                        ].map(style => (
                          <button
                            key={style.id}
                            onClick={() => setFormData(prev => ({ ...prev, hero_style: style.id }))}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              formData.hero_style === style.id ? 'border-blue-500 bg-blue-50' : 'border-gray-100'
                            }`}
                          >
                            <p className="font-medium text-sm">{style.label}</p>
                            <p className="text-[10px] text-gray-500">{style.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Hero Title & Subtitle */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Hero Title</label>
                      <input
                        type="text"
                        value={formData.hero_title}
                        onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        placeholder="Making a Difference Together"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Hero Subtitle</label>
                      <input
                        type="text"
                        value={formData.hero_subtitle}
                        onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        placeholder="Join us in creating positive social impact"
                      />
                    </div>

                    {/* Text Position Controls */}
                    <div className="bg-blue-50 rounded-xl p-4">
                      <label className="block text-sm font-bold text-gray-900 mb-3">📍 Text Position</label>
                      <p className="text-xs text-gray-500 mb-3">Click on the grid or drag in the preview to position your text</p>
                      
                      {/* Position Grid */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {[
                          { x: 20, y: 30, label: 'Top Left' },
                          { x: 50, y: 30, label: 'Top Center' },
                          { x: 80, y: 30, label: 'Top Right' },
                          { x: 20, y: 50, label: 'Middle Left' },
                          { x: 50, y: 50, label: 'Center' },
                          { x: 80, y: 50, label: 'Middle Right' },
                          { x: 20, y: 70, label: 'Bottom Left' },
                          { x: 50, y: 70, label: 'Bottom Center' },
                          { x: 80, y: 70, label: 'Bottom Right' }
                        ].map((pos, i) => (
                          <button
                            key={i}
                            onClick={() => setFormData(prev => ({ 
                              ...prev, 
                              hero_text_position: { x: pos.x, y: pos.y },
                              hero_text_align: pos.x < 40 ? 'left' : pos.x > 60 ? 'right' : 'center'
                            }))}
                            className={`p-2 rounded-lg border-2 text-xs font-medium transition-all ${
                              Math.abs(formData.hero_text_position.x - pos.x) < 15 && 
                              Math.abs(formData.hero_text_position.y - pos.y) < 15
                                ? 'border-blue-500 bg-blue-100 text-blue-700' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {pos.label}
                          </button>
                        ))}
                      </div>

                      {/* Text Alignment */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Text Alignment</label>
                        <div className="flex gap-2">
                          {[
                            { id: 'left', icon: '◀', label: 'Left' },
                            { id: 'center', icon: '●', label: 'Center' },
                            { id: 'right', icon: '▶', label: 'Right' }
                          ].map(align => (
                            <button
                              key={align.id}
                              onClick={() => setFormData(prev => ({ ...prev, hero_text_align: align.id }))}
                              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                                formData.hero_text_align === align.id
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {align.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Stock Hero Images */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">🆓 Free Hero Images</label>
                      <p className="text-xs text-gray-500 mb-3">Select a charity-related image for your hero section</p>
                      <div className="grid grid-cols-4 gap-2">
                        {stockHeroImages.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => selectStockImage(img.url)}
                            className={`relative rounded-lg overflow-hidden aspect-video border-2 transition-all ${
                              formData.hero_image_url === img.url ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-gray-300'
                            }`}
                          >
                            <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 flex items-end p-1">
                              <span className="text-[9px] text-white font-medium">{img.label}</span>
                            </div>
                            {formData.hero_image_url === img.url && (
                              <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs">✓</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Upload */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Or Upload Custom Image</label>
                      <div className="border-2 border-dashed rounded-lg p-4 text-center" style={{ borderColor: 'var(--border-subtle)' }}>
                        <label className="cursor-pointer">
                          <p className="text-sm text-gray-500">📤 Click to upload custom hero image</p>
                          <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'hero_image_url')} className="hidden" />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* LAYOUT TAB - Section Order */}
                {activeTab === 'layout' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-900">Page Layout</h3>
                    <p className="text-sm text-gray-500">
                      Arrange the order of sections on your Impact Website. Drag to reorder or use the arrows.
                    </p>

                    {/* Section Order List */}
                    <div className="space-y-2">
                      {formData.section_order.map((sectionId, index) => {
                        const section = sectionConfig.find(s => s.id === sectionId);
                        if (!section) return null;
                        
                        // Check if section is enabled
                        const isEnabled = 
                          (sectionId === 'stats' && formData.show_stats) ||
                          (sectionId === 'about' && formData.show_about) ||
                          (sectionId === 'trends' && formData.show_trends) ||
                          (sectionId === 'leaderboard' && formData.show_leaderboard) ||
                          (sectionId === 'testimonials' && formData.show_testimonials) ||
                          (sectionId === 'featured' && formData.show_featured_section && formData.featured_campaign_ids.length > 0) ||
                          (sectionId === 'campaigns');
                        
                        return (
                          <div
                            key={sectionId}
                            draggable
                            onDragStart={() => handleSectionDragStart(sectionId)}
                            onDragOver={handleSectionDragOver}
                            onDrop={() => handleSectionDrop(sectionId)}
                            className={`flex items-center gap-3 p-3 border-2 rounded-xl transition-all ${
                              draggedSection === sectionId 
                                ? 'border-blue-500 bg-blue-50 opacity-50' 
                                : isEnabled
                                  ? 'border-gray-200 bg-white hover:border-blue-300'
                                  : 'border-gray-100 bg-gray-50 opacity-60'
                            }`}
                          >
                            {/* Drag Handle */}
                            <div className="cursor-move text-gray-400 hover:text-gray-600">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"/>
                              </svg>
                            </div>
                            
                            {/* Position Number */}
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white"
                              style={{ background: isEnabled ? formData.primary_color: 'var(--text-muted)' }}
                            >
                              {index + 1}
                            </div>
                            
                            {/* Section Info */}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{section.icon}</span>
                                <span className="font-medium text-gray-900">{section.label.replace(section.icon + ' ', '')}</span>
                                {!isEnabled && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">
                                    Hidden
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">{section.description}</p>
                            </div>
                            
                            {/* Arrow Buttons */}
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => moveSectionUp(sectionId)}
                                disabled={index === 0}
                                className={`p-1 rounded transition-all ${
                                  index === 0 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-blue-600'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => moveSectionDown(sectionId)}
                                disabled={index === formData.section_order.length - 1}
                                className={`p-1 rounded transition-all ${
                                  index === formData.section_order.length - 1 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-blue-600'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Quick Tips */}
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-sm font-medium text-blue-800 mb-2">💡 Tips:</p>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• Drag sections or use arrows to reorder</li>
                        <li>• Grayed out sections are currently hidden</li>
                        <li>• Enable sections in their respective tabs</li>
                        <li>• Hero and Footer positions are fixed</li>
                      </ul>
                    </div>

                    {/* Reset Order Button */}
                    <button
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        section_order: ['stats', 'about', 'trends', 'leaderboard', 'testimonials', 'featured', 'campaigns'] 
                      }))}
                      className="w-full py-2 border-2 border-dashed rounded-lg text-sm font-medium text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-all"
                      style={{ borderColor: 'var(--border-subtle)' }}
                    >
                      🔄 Reset to Default Order
                    </button>
                  </div>
                )}

                {/* CONTENT TAB */}
                {activeTab === 'content' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Page Content</h3>
                      <button
                        onClick={() => generateSection('about')}
                        disabled={generatingSection === 'about'}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 transition-all disabled:opacity-50 flex items-center gap-1"
                        data-testid="generate-about-btn"
                      >
                        {generatingSection === 'about' ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Generating...
                          </>
                        ) : (
                          <>✨ Generate with AI</>
                        )}
                      </button>
                    </div>
                    
                    {/* About Section Toggle */}
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-gray-900">Show About Section</p>
                        <p className="text-sm text-gray-500">Display company information</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.show_about}
                        onChange={(e) => setFormData({ ...formData, show_about: e.target.checked })}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                    </label>

                    {formData.show_about && (
                      <>
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2">About Your Company</label>
                          <textarea
                            value={formData.about_text}
                            onChange={(e) => setFormData({ ...formData, about_text: e.target.value })}
                            className="w-full px-4 py-3 border rounded-lg h-32"
                            style={{ borderColor: 'var(--border-subtle)' }}
                            placeholder="Tell visitors about your company's commitment to social impact..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2">Mission Statement</label>
                          <input
                            type="text"
                            value={formData.mission_statement}
                            onChange={(e) => setFormData({ ...formData, mission_statement: e.target.value })}
                            className="w-full px-4 py-3 border rounded-lg"
                            style={{ borderColor: 'var(--border-subtle)' }}
                            placeholder="Our mission is to..."
                          />
                        </div>

                        {/* Company Image */}
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2">Company/Team Photo</label>
                          <div className="border-2 border-dashed rounded-lg p-4 text-center" style={{ borderColor: 'var(--border-subtle)' }}>
                            {formData.company_image_url ? (
                              <div className="space-y-2">
                                <img src={formData.company_image_url} alt="Company" className="h-32 mx-auto object-cover rounded-lg" />
                                <div className="flex gap-2 justify-center">
                                  <label className="px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer text-blue-600 hover:bg-blue-50">
                                    Change
                                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'company_image_url')} className="hidden" />
                                  </label>
                                  <button 
                                    onClick={() => setFormData(prev => ({ ...prev, company_image_url: '' }))}
                                    className="px-3 py-1.5 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <label className="cursor-pointer block">
                                <p className="text-2xl mb-1">📷</p>
                                <p className="text-sm text-gray-500">Upload team or office photo</p>
                                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'company_image_url')} className="hidden" />
                              </label>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* CTA Text */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Call to Action Button Text</label>
                      <input
                        type="text"
                        value={formData.call_to_action_text}
                        onChange={(e) => setFormData({ ...formData, call_to_action_text: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        placeholder="Donate Now"
                      />
                    </div>
                  </div>
                )}

                {/* TRENDS & NEWS TAB */}
                {activeTab === 'trends' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Charity Trends & News</h3>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => generateSection('trends')}
                          disabled={generatingSection === 'trends'}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 transition-all disabled:opacity-50 flex items-center gap-1"
                          data-testid="generate-trends-btn"
                        >
                          {generatingSection === 'trends' ? (
                            <>
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Generating...
                            </>
                          ) : (
                            <>✨ Generate with AI</>
                          )}
                        </button>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.show_trends}
                            onChange={(e) => setFormData({ ...formData, show_trends: e.target.checked })}
                            className="w-5 h-5 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-600">Show on page</span>
                        </label>
                      </div>
                    </div>

                    <p className="text-sm text-gray-500">Add compelling charity trends, industry statistics, and news to engage visitors and demonstrate the importance of corporate giving.</p>

                    {/* Section Title */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Section Title</label>
                      <input
                        type="text"
                        value={formData.trends_title}
                        onChange={(e) => setFormData({ ...formData, trends_title: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        placeholder="Charity Trends & Insights"
                      />
                    </div>

                    {/* Charity Trends */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-bold text-gray-900">Key Trends</label>
                        <button
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            charity_trends: [...(prev.charity_trends || []), { title: '', description: '', icon: '📈' }]
                          }))}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          + Add Trend
                        </button>
                      </div>
                      
                      {(formData.charity_trends || []).length === 0 ? (
                        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 text-sm">
                          No trends added yet. Click &quot;Generate with AI&quot; or add manually.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {(formData.charity_trends || []).map((trend, index) => (
                            <div key={index} className="p-4 border rounded-lg space-y-3" style={{ borderColor: 'var(--border-subtle)' }}>
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Trend #{index + 1}</span>
                                <button 
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    charity_trends: prev.charity_trends.filter((_, i) => i !== index)
                                  }))}
                                  className="text-red-500 text-sm hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                              <div className="grid grid-cols-[60px_1fr] gap-3">
                                <input
                                  type="text"
                                  placeholder="📈"
                                  value={trend.icon}
                                  onChange={(e) => {
                                    const newTrends = [...formData.charity_trends];
                                    newTrends[index].icon = e.target.value;
                                    setFormData(prev => ({ ...prev, charity_trends: newTrends }));
                                  }}
                                  className="px-3 py-2 border rounded-lg text-center text-xl"
                                  style={{ borderColor: 'var(--border-subtle)' }}
                                />
                                <input
                                  type="text"
                                  placeholder="Trend Title"
                                  value={trend.title}
                                  onChange={(e) => {
                                    const newTrends = [...formData.charity_trends];
                                    newTrends[index].title = e.target.value;
                                    setFormData(prev => ({ ...prev, charity_trends: newTrends }));
                                  }}
                                  className="px-3 py-2 border rounded-lg"
                                  style={{ borderColor: 'var(--border-subtle)' }}
                                />
                              </div>
                              <textarea
                                placeholder="Brief description of this trend..."
                                value={trend.description}
                                onChange={(e) => {
                                  const newTrends = [...formData.charity_trends];
                                  newTrends[index].description = e.target.value;
                                  setFormData(prev => ({ ...prev, charity_trends: newTrends }));
                                }}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                style={{ borderColor: 'var(--border-subtle)' }}
                                rows={2}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Industry Statistics */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-bold text-gray-900">Industry Statistics</label>
                        <button
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            industry_stats: [...(prev.industry_stats || []), { value: '', label: '', source: '' }]
                          }))}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          + Add Stat
                        </button>
                      </div>
                      
                      {(formData.industry_stats || []).length === 0 ? (
                        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 text-sm">
                          No statistics added yet. Click &quot;Generate with AI&quot; or add manually.
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {(formData.industry_stats || []).map((stat, index) => (
                            <div key={index} className="p-3 border rounded-lg" style={{ borderColor: 'var(--border-subtle)' }}>
                              <div className="flex justify-between items-start mb-2">
                                <input
                                  type="text"
                                  placeholder="$500B"
                                  value={stat.value}
                                  onChange={(e) => {
                                    const newStats = [...formData.industry_stats];
                                    newStats[index].value = e.target.value;
                                    setFormData(prev => ({ ...prev, industry_stats: newStats }));
                                  }}
                                  className="text-xl font-bold w-24 border-b border-transparent focus:border-blue-500 outline-none"
                                  style={{ color: formData.primary_color }}
                                />
                                <button 
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    industry_stats: prev.industry_stats.filter((_, i) => i !== index)
                                  }))}
                                  className="text-red-400 text-xs hover:text-red-600"
                                >
                                  ✕
                                </button>
                              </div>
                              <input
                                type="text"
                                placeholder="Annual charitable giving"
                                value={stat.label}
                                onChange={(e) => {
                                  const newStats = [...formData.industry_stats];
                                  newStats[index].label = e.target.value;
                                  setFormData(prev => ({ ...prev, industry_stats: newStats }));
                                }}
                                className="text-sm text-gray-600 w-full border-b border-transparent focus:border-blue-500 outline-none"
                              />
                              <input
                                type="text"
                                placeholder="Source: Giving USA"
                                value={stat.source}
                                onChange={(e) => {
                                  const newStats = [...formData.industry_stats];
                                  newStats[index].source = e.target.value;
                                  setFormData(prev => ({ ...prev, industry_stats: newStats }));
                                }}
                                className="text-xs text-gray-400 w-full mt-1 border-b border-transparent focus:border-blue-500 outline-none"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* News Items */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-bold text-gray-900">Latest News & Updates</label>
                        <button
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            news_items: [...(prev.news_items || []), { headline: '', summary: '', date: new Date().toISOString().split('T')[0], link: '' }]
                          }))}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          + Add News
                        </button>
                      </div>
                      
                      {(formData.news_items || []).length === 0 ? (
                        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 text-sm">
                          No news items added yet. Click &quot;Generate with AI&quot; to add relevant industry news.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {(formData.news_items || []).map((news, index) => (
                            <div key={index} className="p-4 border rounded-lg space-y-2" style={{ borderColor: 'var(--border-subtle)' }}>
                              <div className="flex justify-between items-center">
                                <input
                                  type="date"
                                  value={news.date}
                                  onChange={(e) => {
                                    const newNews = [...formData.news_items];
                                    newNews[index].date = e.target.value;
                                    setFormData(prev => ({ ...prev, news_items: newNews }));
                                  }}
                                  className="text-xs text-gray-500 border rounded px-2 py-1"
                                />
                                <button 
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    news_items: prev.news_items.filter((_, i) => i !== index)
                                  }))}
                                  className="text-red-500 text-sm hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                              <input
                                type="text"
                                placeholder="News Headline"
                                value={news.headline}
                                onChange={(e) => {
                                  const newNews = [...formData.news_items];
                                  newNews[index].headline = e.target.value;
                                  setFormData(prev => ({ ...prev, news_items: newNews }));
                                }}
                                className="w-full px-3 py-2 border rounded-lg font-medium"
                                style={{ borderColor: 'var(--border-subtle)' }}
                              />
                              <textarea
                                placeholder="Brief summary..."
                                value={news.summary}
                                onChange={(e) => {
                                  const newNews = [...formData.news_items];
                                  newNews[index].summary = e.target.value;
                                  setFormData(prev => ({ ...prev, news_items: newNews }));
                                }}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                style={{ borderColor: 'var(--border-subtle)' }}
                                rows={2}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* FEATURED CHARITIES TAB */}
                {activeTab === 'featured' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Featured Charities</h3>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.show_featured_section}
                          onChange={(e) => setFormData({ ...formData, show_featured_section: e.target.checked })}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-600">Show on page</span>
                      </label>
                    </div>

                    <p className="text-sm text-gray-500">
                      Select up to 3 campaigns to feature prominently on your Impact Website. Drag to reorder.
                    </p>

                    {/* Section Title */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Section Title</label>
                      <input
                        type="text"
                        value={formData.featured_section_title}
                        onChange={(e) => setFormData({ ...formData, featured_section_title: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        placeholder="Featured Causes"
                      />
                    </div>

                    {/* Selected Featured Campaigns (Drag to Reorder) */}
                    {formData.featured_campaign_ids.length > 0 && (
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">
                          Featured Order (Drag to reorder)
                        </label>
                        <div className="space-y-2">
                          {featuredCampaigns.map((campaign, index) => (
                            <div
                              key={campaign.campaign_id}
                              draggable
                              onDragStart={() => handleDragStart(campaign.campaign_id)}
                              onDragOver={handleDragOver}
                              onDrop={() => handleDrop(campaign.campaign_id)}
                              className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-move transition-all ${
                                draggedCampaign === campaign.campaign_id 
                                  ? 'border-blue-500 bg-blue-50 opacity-50' 
                                  : 'border-gray-200 bg-white hover:border-blue-300'
                              }`}
                            >
                              <div className="flex items-center justify-center w-8 h-8 rounded-full text-white font-bold text-sm"
                                style={{ background: formData.primary_color }}>
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 text-sm">{campaign.title}</p>
                                <p className="text-xs text-gray-500">{campaign.charity_name}</p>
                              </div>
                              <button
                                onClick={() => toggleFeaturedCampaign(campaign.campaign_id)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Campaigns to Feature */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Available Campaigns ({3 - formData.featured_campaign_ids.length} slots remaining)
                      </label>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {campaigns
                          .filter(c => !formData.featured_campaign_ids.includes(c.campaign_id))
                          .map(campaign => (
                            <div
                              key={campaign.campaign_id}
                              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-all"
                              style={{ borderColor: 'var(--border-subtle)' }}
                            >
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 text-sm">{campaign.title}</p>
                                <p className="text-xs text-gray-500">{campaign.charity_name}</p>
                                <div className="flex gap-3 mt-1">
                                  <span className="text-xs text-green-600">
                                    ${(campaign.raised_amount || 0).toLocaleString()} raised
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    ESG: {campaign.esg_score || 'N/A'}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => toggleFeaturedCampaign(campaign.campaign_id)}
                                disabled={formData.featured_campaign_ids.length >= 3}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                  formData.featured_campaign_ids.length >= 3
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'text-white hover:opacity-90'
                                }`}
                                style={{ 
                                  background: formData.featured_campaign_ids.length >= 3 
                                    ? undefined 
                                    : formData.primary_color 
                                }}
                              >
                                + Feature
                              </button>
                            </div>
                          ))}
                        {campaigns.length === 0 && (
                          <p className="text-sm text-gray-400 text-center py-4">
                            No campaigns available. Create campaigns first.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Preview of Featured Section */}
                    {formData.featured_campaign_ids.length > 0 && (
                      <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-blue-700 mb-2">Preview:</p>
                        <p className="text-sm text-gray-600">
                          {featuredCampaigns.length} featured campaign{featuredCampaigns.length !== 1 ? 's' : ''} will appear in a highlighted section with donate buttons.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* TESTIMONIALS TAB */}
                {activeTab === 'testimonials' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Testimonials</h3>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => generateSection('testimonials')}
                          disabled={generatingSection === 'testimonials'}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 transition-all disabled:opacity-50 flex items-center gap-1"
                          data-testid="generate-testimonials-btn"
                        >
                          {generatingSection === 'testimonials' ? (
                            <>
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Generating...
                            </>
                          ) : (
                            <>✨ Generate Samples</>
                          )}
                        </button>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.show_testimonials}
                            onChange={(e) => setFormData({ ...formData, show_testimonials: e.target.checked })}
                            className="w-5 h-5 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-600">Show on page</span>
                        </label>
                      </div>
                    </div>

                    <p className="text-sm text-gray-500">Add testimonials from employees, partners, or beneficiaries. Use AI to generate sample testimonials that you can customize.</p>

                    {formData.testimonials.map((testimonial, index) => (
                      <div key={index} className="p-4 border rounded-lg space-y-3" style={{ borderColor: 'var(--border-subtle)' }}>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">
                            {testimonial.name?.includes('Sample') || index < 3 && formData.testimonials.length === 3 ? '📝 Sample Testimonial' : ''} Testimonial #{index + 1}
                          </span>
                          <button onClick={() => removeTestimonial(index)} className="text-red-500 text-sm hover:underline">Remove</button>
                        </div>
                        <input
                          type="text"
                          placeholder="Name"
                          value={testimonial.name}
                          onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                          style={{ borderColor: 'var(--border-subtle)' }}
                        />
                        <input
                          type="text"
                          placeholder="Role (e.g., Employee, Partner, Beneficiary)"
                          value={testimonial.role}
                          onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                          style={{ borderColor: 'var(--border-subtle)' }}
                        />
                        <textarea
                          placeholder="Their testimonial..."
                          value={testimonial.text}
                          onChange={(e) => updateTestimonial(index, 'text', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm h-20"
                          style={{ borderColor: 'var(--border-subtle)' }}
                        />
                        <div>
                          <label className="text-xs text-gray-500">Rating</label>
                          <div className="flex gap-1">
                            {[1,2,3,4,5].map(star => (
                              <button
                                key={star}
                                onClick={() => updateTestimonial(index, 'rating', star)}
                                className={`text-xl ${star <= (testimonial.rating || 5) ? 'text-yellow-400' : 'text-gray-300'}`}
                              >
                                ★
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={addTestimonial}
                      className="w-full py-3 border-2 border-dashed rounded-lg text-sm font-medium text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-all"
                      style={{ borderColor: 'var(--border-subtle)' }}
                    >
                      + Add Testimonial
                    </button>
                  </div>
                )}

                {/* LEADERBOARD TAB */}
                {activeTab === 'leaderboard' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Employee Leaderboard</h3>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.show_leaderboard}
                          onChange={(e) => setFormData({ ...formData, show_leaderboard: e.target.checked })}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-600">Show on page</span>
                      </label>
                    </div>

                    <p className="text-sm text-gray-500">Celebrate your top contributors! Data is automatically pulled from donations.</p>

                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Section Title</label>
                      <input
                        type="text"
                        value={formData.leaderboard_title}
                        onChange={(e) => setFormData({ ...formData, leaderboard_title: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        placeholder="Our Impact Champions"
                      />
                    </div>

                    {/* Preview */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">Current Top Contributors:</p>
                      {employeeLeaderboard.slice(0, 3).map((person, i) => (
                        <div key={i} className="flex items-center gap-3 py-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                            i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : 'bg-amber-600'
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{person.name}</p>
                            <p className="text-xs text-gray-500">${person.total.toLocaleString()} donated</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ADVANCED TAB */}
                {activeTab === 'advanced' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-900">Advanced Settings</h3>
                    
                    {/* Stats Toggle */}
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-gray-900">Show Impact Stats</p>
                        <p className="text-sm text-gray-500">Display total raised, campaigns, ESG score</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.show_stats}
                        onChange={(e) => setFormData({ ...formData, show_stats: e.target.checked })}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                    </label>

                    {/* Social Links */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Social Media Links</label>
                      <div className="space-y-2">
                        {['linkedin', 'twitter', 'facebook', 'instagram'].map(platform => (
                          <div key={platform} className="flex items-center gap-2">
                            <span className="w-24 text-sm text-gray-600 capitalize">{platform}</span>
                            <input
                              type="url"
                              value={formData.social_links[platform] || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                social_links: { ...prev.social_links, [platform]: e.target.value }
                              }))}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                              style={{ borderColor: 'var(--border-subtle)' }}
                              placeholder={`https://${platform}.com/...`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer Text */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Custom Footer Text</label>
                      <textarea
                        value={formData.footer_text}
                        onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg h-20"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        placeholder="© 2024 Your Company. All rights reserved."
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Live Preview */}
            <div className="col-span-7">
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden sticky top-24">
                <div className="px-4 py-2 bg-gray-100 flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2">Live Preview</span>
                </div>
                <div className="h-[calc(100vh-240px)] overflow-y-auto bg-white alt-no-theme">
                  <ImpactPreview 
                    formData={formData} 
                    business={business} 
                    campaigns={campaigns} 
                    leaderboard={employeeLeaderboard}
                    totalRaised={totalRaised}
                    totalGoal={totalGoal}
                    avgESG={avgESG}
                    compact
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteBuilder;
